import React, { FC, useCallback, useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, FlexDynamic } from '../../atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';
import { CheckboxInput, CheckboxInputProps } from './CheckboxInput';
import { Option, validateRadioCheckOptionsProp } from './utils';

const SELECT_ALL_VALUE = 'selectAll';

interface UnconnectedCheckboxGroupProps {
  /** the list of values currently selected in the group */
  value: string[];
  /** the list of checkbox options in the group */
  options: Option[];
  /** callback invoked when an option is selected or deselected */
  onChange: (newValues: string[]) => void;
  /** disable all checkboxes from changing */
  disabled?: boolean;
  /** size for the Checkbox elements */
  size?: CheckboxInputProps['size'];
  /** name for the form field in the form values */
  name: string;
  /** whether to include a select all option */
  withSelectAll?: boolean;
}

/**
 * A group of checkbox inputs of which all are tied to the same field in a form
 * (an array of string values)
 * you need to provide the value and onChange handlers to manage the state of the group
 * do not add state to this component
 * @name UnconnectedCheckboxGroup
 * @description use this if you need to compose complex inter-related form elements that include a checkbox group
 *
 * @example
 * const [selectedValue, setSelectedValue] = useState<string[]>([]); // state management in parent component
 * <UnconnectedCheckboxGroup
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 *  value={selectedValue}
 *  onChange={setSelectedValue}
 * />
 */
const UnconnectedCheckboxGroup: FC<UnconnectedCheckboxGroupProps> = ({
  onChange: onChangeProp,
  options: optionsProp,
  value,
  disabled = false,
  size = 'lg',
  name,
  withSelectAll = false,
}) => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    let opts = Array.isArray(optionsProp) ? optionsProp : [];

    if (withSelectAll && opts.length > 0) {
      opts = [{ value: SELECT_ALL_VALUE, label: t('common:form.label.selectAll') }, ...opts];
    }

    return validateRadioCheckOptionsProp({ options: opts, componentName: 'UnconnectedCheckboxGroup' });
  }, [withSelectAll, optionsProp, t]);

  const areAllChecked = useMemo(() => {
    const uncheckedOpts = options.filter((o) => o.value !== SELECT_ALL_VALUE && !value.includes(o.value));
    return uncheckedOpts.length === 0;
  }, [options, value]);

  const onChange = useCallback(
    (newVal: string) => {
      const optsWithoutSelectAll = options.filter((o) => o.value !== SELECT_ALL_VALUE);
      if (newVal === SELECT_ALL_VALUE && areAllChecked) {
        onChangeProp([]);
        return;
      }

      if (newVal === SELECT_ALL_VALUE) {
        onChangeProp(optsWithoutSelectAll.map((o) => o.value));
        return;
      }

      if (value.includes(newVal)) {
        onChangeProp(value.filter((v) => v !== newVal));
      } else {
        onChangeProp([...value, newVal]);
      }
    },
    [areAllChecked, onChangeProp, options, value]
  );

  return (
    <FlexDynamic data-testid={`checkbox-group-${name}`} direction="column" gap="2" mt="2" w="full">
      {options.map((option) => (
        <React.Fragment key={option.value}>
          <CheckboxInput
            value={option.value}
            label={option.label}
            checked={option.value === SELECT_ALL_VALUE && areAllChecked ? true : value?.includes(option.value)}
            onChange={(newValue) => onChange(newValue)}
            disabled={disabled}
            size={size}
            name={name}
          />
          {option.value === SELECT_ALL_VALUE && (
            <BasicContainer w="full" data-testid="select-all-checkbox-divider" borderPlacement="bottom" />
          )}
        </React.Fragment>
      ))}
    </FlexDynamic>
  );
};

export interface WrappedUnconnectedCheckboxGroupProps {
  inputProps: UnconnectedCheckboxGroupProps;
  wrapperProps?: FieldWrapperProps;
}

export const WrappedUnconnectedCheckboxGroup: FC<WrappedUnconnectedCheckboxGroupProps> = ({
  wrapperProps,
  inputProps,
}) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <UnconnectedCheckboxGroup {...inputProps} />
    </FieldWrapper>
  );
};

type ControlledCheckboxGroupProps = Omit<UnconnectedCheckboxGroupProps, 'onChange' | 'value'>;

export interface CheckboxGroupInputProps {
  /** full props for the underlying UnconnectedCheckboxGroup component */
  inputProps?: Omit<ControlledCheckboxGroupProps, 'options'>;
  /** full props for the underlying FieldWrapper component */
  wrapperProps?: FieldWrapperProps;
  /** array of configuration for the Checkbox options in the group */
  options: ControlledCheckboxGroupProps['options'];
  /** name of the form field in the Formik form values */
  name: string;
  /** disable the Checkbox inputs in the group */
  disabled?: boolean;
  /** label for the form field Checkboxgroup */
  label: FieldWrapperProps['label'];
  /** help text for the form field */
  helpText?: FieldWrapperProps['helpText'];
  /** size for the Checkbox elements */
  size?: CheckboxInputProps['size'];
  /** include a select all option */
  withSelectAll?: boolean;
}

/**
 * A group of checkbox inputs of which all are tied to the same field in a form
 * (an array of string values)
 * @name CheckboxGroupInput
 * @description this should be used as the normal component for groups of checkbox inputs for all Formik forms.
 * it will automatically render any validation errors and will correctly set a11y roles for the elements
 *
 * @example
 * <CheckboxGroupInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 * />
 *
 * @example with selectAll enabled
 * <CheckboxGroupInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 *  withSelectAll
 * />
 */
export const CheckboxGroupInput: FC<CheckboxGroupInputProps> = ({
  disabled,
  helpText,
  inputProps,
  label,
  name,
  options,
  wrapperProps,
  size = 'lg',
  withSelectAll = false,
}) => {
  const [input, meta, helpers] = useField<string[]>(name);
  const { value: formikValue } = input;
  const { error } = meta;
  const hasError = !!error;
  const wrapperValidation = hasError ? { validation: { valid: !hasError, message: error?.toString() } } : {};
  const inputValue = Array.isArray(formikValue) ? formikValue : [];

  const onChange = (newVal: string[]) => {
    helpers.setValue(newVal);
  };

  return (
    <WrappedUnconnectedCheckboxGroup
      inputProps={{
        ...inputProps,
        name,
        labelId: `${name}-checkboxgroup-label`,
        options,
        onChange,
        value: inputValue,
        disabled,
        size,
        withSelectAll,
      }}
      wrapperProps={{ ...wrapperProps, ...wrapperValidation, id: name, disabled, label, helpText }}
    />
  );
};
